// "Mis tareas" — portal del PROFESOR: solo lo asignado a MÍ (GET /tasks?mine=1).
// Si no hay pendientes, el panel NO se renderiza (pedido de Carlos 2026-07-01).
// El profesor puede completar u omitir-con-motivo (nivel operate); no edita ni asigna.
import { useEffect, useState } from 'react';
import { ListTodo, Check, Flag, Repeat, CircleSlash } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { listTasks, updateTask, Task } from './api';

function dueChip(t: Task): { label: string; cls: string } | null {
  if (t.days_until == null) return null;
  if (t.days_until < 0) return { label: t.days_until === -1 ? 'venció ayer' : `venció hace ${-t.days_until}d`, cls: 'bg-rose-500/15 text-rose-600' };
  if (t.days_until === 0) return { label: 'para hoy', cls: 'bg-amber-500/15 text-amber-600' };
  if (t.days_until === 1) return { label: 'mañana', cls: 'bg-brand-blue/10 text-brand-blue' };
  return { label: `en ${t.days_until}d`, cls: 'bg-surface-2 text-content-3' };
}

export default function MyTasksPanel() {
  const toast = useToast();
  const { hasModule } = useAuth();
  const [tasks, setTasks] = useState<Task[] | null>(null);

  const load = () => listTasks('home', true)
    .then((r) => setTasks(r.data.filter((t) => t.status === 'open')))
    .catch(() => setTasks([]));
  useEffect(() => { if (hasModule('tasks')) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sin módulo, cargando o sin pendientes → no ocupar espacio en el portal.
  if (!hasModule('tasks') || !tasks || tasks.length === 0) return null;

  const complete = async (t: Task) => {
    setTasks((prev) => prev?.filter((x) => x.task_id !== t.task_id) || null);
    try {
      await updateTask(t.task_id, { status: 'done' });
      toast.success('Tarea completada 💪');
    } catch (e: any) { toast.error(e?.message || 'No se pudo completar'); }
    load();
  };

  const skip = async (t: Task) => {
    const reason = window.prompt(`Omitir "${t.title}" — ¿por qué no se pudo?`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error('El motivo es obligatorio para omitir'); return; }
    try {
      await updateTask(t.task_id, { status: 'skipped', skip_reason: reason.trim() });
    } catch (e: any) { toast.error(e?.message || 'No se pudo omitir'); }
    load();
  };

  return (
    <div className="rounded-2xl border border-accent/30 bg-surface p-4 shadow-sm">
      <h2 className="mb-2 flex items-center gap-2 font-display text-base font-bold text-content">
        <ListTodo size={17} className="text-accent-ink" /> Mis tareas
        <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent-ink">{tasks.length}</span>
      </h2>
      <ul className="space-y-1.5">
        {tasks.map((t) => {
          const chip = dueChip(t);
          return (
            <li key={t.task_id} className="flex items-start gap-2.5 rounded-lg px-1 py-1.5">
              <button onClick={() => complete(t)} title="Completar"
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-content-3 transition hover:border-emerald-500 hover:bg-emerald-500/10">
                <Check size={14} className="opacity-0 transition hover:opacity-100" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-content">
                  {t.template_id != null && <Repeat size={11} className="mr-1 inline text-content-3" />}
                  {t.priority === 'high' && <Flag size={11} className="mr-1 inline text-rose-500" />}
                  {t.title}
                </p>
                {t.description && <p className="mt-0.5 text-xs text-content-3">{t.description}</p>}
              </div>
              {chip && <span className={`mt-1 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.cls}`}>{chip.label}</span>}
              {t.template_id != null && (
                <button onClick={() => skip(t)} title="No se pudo hacer (omitir con motivo)"
                  className="mt-0.5 shrink-0 p-1 text-content-3 transition hover:text-amber-500">
                  <CircleSlash size={15} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
